import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  Avatar,
  Card,
  CardContent,
  Divider,
  Alert,
  Snackbar,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Verified as VerifiedIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as WebsiteIcon
} from '@mui/icons-material';
import { RocketLaunch as BoostIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import DatabaseService, { UserService } from '../../services/DatabaseService';
import { calculateEmployerCompletion, normalizeEmployerProfile } from '../../utils/profileCompletion';
import CloudinaryService from '../../services/CloudinaryService';
import ApiService from '../../services/api';
// import { usePayment } from '../../context/PaymentContext';
import { isBoostActive } from '../../utils/boost';
import ProfileBoostPayment from '../../components/profile/ProfileBoostPayment';

const Profile = () => {
  const { user, isAdmin, token, refreshAccessToken } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    companyName: '',
    industry: '',
    companySize: '',
    description: '',
    website: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    logo: '',
    isVerified: false,
    foundedYear: '',
    specialties: [],
    // Representative
    representativeName: '',
    representativePosition: '',
    representativeEmail: '',
    representativePhone: '',
    // Job categories
    jobCategories: [],
    // Verification documents
    businessLicense: [],
    companyRegistration: [],
    taxCertificate: [],
    otherDocuments: []
  });
  const location = useLocation();
  const [isEditing, setIsEditing] = useState(!!(location.state && location.state.openEdit));
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [userBoost, setUserBoost] = useState({ boosted: false, boostExpiresAt: null, boostStatus: null });
  const [profileBoostOpen, setProfileBoostOpen] = useState(false);

  const industries = [
    'Technology', 'Healthcare', 'Finance', 'Education', 'Manufacturing',
    'Retail', 'Construction', 'Transportation', 'Hospitality', 'Media',
    'Real Estate', 'Consulting', 'Non-profit', 'Government', 'Other'
  ];

  const companySizes = [
    '1-10 employees',
    '11-50 employees',
    '51-200 employees',
    '201-500 employees',
    '501-1000 employees',
    '1000+ employees'
  ];

  // Common job categories used across the app
  const jobCategoryOptions = [
    'Technology', 'Healthcare', 'Finance', 'Education', 'Marketing',
    'Sales', 'Human Resources', 'Operations', 'Customer Service', 'Design',
    'Engineering', 'Legal', 'Consulting', 'Manufacturing', 'Retail',
    'Hospitality', 'Transportation', 'Real Estate', 'Media', 'Non-Profit',
    'Government', 'Other'
  ];

  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      const userData = await DatabaseService.getUserProfile(user._id);
      
      const normalized = normalizeEmployerProfile(userData);
      const employer = userData.employerProfile || {};
      const rep = employer.representative || {};
      const addr = normalized.address || {};
      setProfile({
        companyName: normalized.companyName || '',
        industry: normalized.industry || '',
        companySize: normalized.companySize || '',
        description: normalized.description || '',
        website: normalized.website || '',
        email: normalized.email || '',
        phone: normalized.phone || '',
        address: typeof addr === 'string' ? addr : (addr.street || ''),
        city: (addr.city || normalized.city || ''),
        state: (addr.province || addr.state || normalized.state || ''),
        country: (addr.country || normalized.country || ''),
        zipCode: (addr.postalCode || addr.zipCode || normalized.zipCode || ''),
        logo: normalized.logo || '',
        isVerified: !!normalized.isVerified,
        foundedYear: normalized.foundedYear || '',
        specialties: Array.isArray(normalized.specialties) ? normalized.specialties : [],
        representativeName: rep.name || '',
        representativePosition: rep.position || '',
        representativeEmail: rep.email || '',
        representativePhone: rep.phone || '',
        jobCategories: Array.isArray(normalized.jobCategories) ? normalized.jobCategories : [],
        businessLicense: Array.isArray(employer.businessLicense) ? employer.businessLicense : [],
        companyRegistration: Array.isArray(employer.companyRegistration) ? employer.companyRegistration : [],
        taxCertificate: Array.isArray(employer.taxCertificate) ? employer.taxCertificate : [],
        otherDocuments: Array.isArray(employer.otherDocuments) ? employer.otherDocuments : [],
      });
      setUserBoost({
        boosted: !!userData.boosted,
        boostExpiresAt: userData.boostExpiresAt || null,
        boostStatus: userData.boostStatus || null
      });
    } catch (error) {
      console.error('Error loading profile:', error);
      showSnackbar('Error loading profile', 'error');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);


  // Upload helper for verification documents
  const handleDocUpload = async (category, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setLoading(true);
      const folder = `jobit/company_docs/${category}`;
      const upload = await CloudinaryService.uploadFile(file, folder);
      setProfile(prev => ({
        ...prev,
        [category]: [...(prev[category] || []), { name: file.name, url: upload.url }]
      }));
      showSnackbar(`${category} uploaded successfully`, 'success');
    } catch (err) {
      console.error('Upload error:', err);
      showSnackbar('Failed to upload document', 'error');
    } finally {
      setLoading(false);
    }
  };

  const removeDoc = (category, index) => {
    setProfile(prev => {
      const list = [...(prev[category] || [])];
      list.splice(index, 1);
      return { ...prev, [category]: list };
    });
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setLoading(true);
      const folder = `jobit/company_logo`;
      const upload = await CloudinaryService.uploadFile(file, folder);
      setProfile(prev => ({ ...prev, logo: upload.url }));
      showSnackbar('Logo updated successfully', 'success');
    } catch (err) {
      showSnackbar('Failed to upload logo', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      // Ensure an active user session/token for /auth/profile
      let activeToken = localStorage.getItem('token') || localStorage.getItem('adminToken');
      if (!activeToken) {
        try { await refreshAccessToken(); } catch (_) {}
        activeToken = localStorage.getItem('token') || localStorage.getItem('adminToken');
      }
      if (!activeToken) {
        showSnackbar('Session expired. Please log in again.', 'warning');
        setIsEditing(false);
        setLoading(false);
        navigate('/login');
        return;
      }
      
      // Prepare employer payload in wizard-friendly shape; service will map to backend schema
      const street = typeof profile.address === 'string' ? profile.address : (profile.address?.street || '');
      const officeAddressParts = [street, profile.city, profile.state, profile.country, profile.zipCode]
        .filter(Boolean)
        .join(', ');

      const profileData = {
        companyName: profile.companyName,
        industry: profile.industry,
        companySize: profile.companySize,
        website: profile.website,
        companyDescription: profile.description,
        companyLogoUrl: profile.logo,
        officeAddress: officeAddressParts,
        companyPhone: profile.phone,
        companyEmail: profile.email,
        // Representative fields
        representativeName: profile.representativeName || '',
        representativePosition: profile.representativePosition || '',
        representativeEmail: profile.representativeEmail || '',
        representativePhone: profile.representativePhone || '',
        // Job categories
        jobCategories: Array.isArray(profile.jobCategories) ? profile.jobCategories : [],
        // Verification document categories, will be flattened by mapper
        businessLicense: (profile.businessLicense || []).map(d => d.url || d.name).filter(Boolean),
        companyRegistration: (profile.companyRegistration || []).map(d => d.url || d.name).filter(Boolean),
        taxCertificate: (profile.taxCertificate || []).map(d => d.url || d.name).filter(Boolean),
        otherDocuments: (profile.otherDocuments || []).map(d => d.url || d.name).filter(Boolean)
      };

      const response = await UserService.updateUserProfile(user._id, 'employer', profileData);
      try {
        await loadProfile();
      } catch (_) {
        const updatedUser = response?.user || null;
        if (updatedUser) {
          const normalized = normalizeEmployerProfile(updatedUser);
          const addr = normalized.address || {};
          setProfile(prev => ({
            ...prev,
            companyName: normalized.companyName || prev.companyName,
            industry: normalized.industry || prev.industry,
            companySize: normalized.companySize || prev.companySize,
            description: normalized.description || prev.description,
            website: normalized.website || prev.website,
            email: normalized.email || prev.email,
            phone: normalized.phone || prev.phone,
            address: typeof addr === 'string' ? addr : (addr.street || prev.address),
            city: addr.city || normalized.city || prev.city,
            state: addr.province || addr.state || normalized.state || prev.state,
            country: addr.country || normalized.country || prev.country,
            zipCode: addr.postalCode || addr.zipCode || normalized.zipCode || prev.zipCode,
            logo: normalized.logo || prev.logo
          }));
        }
      }
      setIsEditing(false);
      showSnackbar('Profile updated successfully', 'success');
    } catch (error) {
      console.error('Error updating profile:', error);
      showSnackbar('Error updating profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    loadProfile();
  };

  const handleInputChange = (field, value) => {
    if (field === 'address') {
      const isObj = typeof profile.address === 'object' && profile.address !== null;
      if (isObj) {
        const next = { ...profile.address, street: value };
        setProfile(prev => ({ ...prev, address: next }));
        return;
      }
    }
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const [systemSettings, setSystemSettings] = useState(null);

  const handleBoostProfile = async () => {
    try {
      const uid = user?._id || user?.uid;
      const isAdminSession = !!isAdmin;

      // Require a USER session for boost request when not using admin override
      if (!isAdminSession && !token) {
        showSnackbar('Please log in to boost your profile.', 'warning');
        return;
      }

      if (isAdminSession) {
        await ApiService.boostUserProfile(uid, { override: true });
        showSnackbar('Profile boosted successfully (admin override)', 'success');
        // refresh boost state
        const refreshed = await DatabaseService.getUserProfile(uid);
        setUserBoost({
          boosted: !!refreshed.boosted,
          boostExpiresAt: refreshed.boostExpiresAt || null,
          boostStatus: refreshed.boostStatus || null
        });
        return;
      }

      // Non-admin: Gate behind payment if required by settings
      const settings = systemSettings || (await ApiService.getSystemSettings());
      setSystemSettings(prev => prev || settings);
      const paymentsRequired = !!(settings?.payments?.enableBoostPayments);
      if (paymentsRequired) {
        setProfileBoostOpen(true);
        return;
      } else {
        await ApiService.requestUserBoost(uid);
        showSnackbar('Boost request submitted. Admin will approve shortly.', 'success');
      }
      // refresh boost state
      const refreshed = await DatabaseService.getUserProfile(uid);
      setUserBoost({
        boosted: !!refreshed.boosted,
        boostExpiresAt: refreshed.boostExpiresAt || null,
        boostStatus: refreshed.boostStatus || null
      });
    } catch (err) {
      console.error('Error boosting profile:', err);
      showSnackbar('Failed to process boost request', 'error');
    }
  };

  const handleProfileBoostPaid = async (transaction) => {
    try {
      const uid = user?._id || user?.uid;
      await ApiService.requestUserBoost(uid, { paymentTransactionId: transaction?.transactionId });
      showSnackbar('Payment recorded and boost request submitted.', 'success');
      setProfileBoostOpen(false);
      const refreshed = await DatabaseService.getUserProfile(uid);
      setUserBoost({
        boosted: !!refreshed.boosted,
        boostExpiresAt: refreshed.boostExpiresAt || null,
        boostStatus: refreshed.boostStatus || null
      });
    } catch (error) {
      console.error('Error finalizing profile boost after payment:', error);
      showSnackbar('Payment succeeded but boost request failed. Please contact support.', 'error');
    }
  };

  const canRequestBoost = (() => {
    const active = isBoostActive(userBoost?.boosted, userBoost?.boostExpiresAt);
    const status = userBoost?.boostStatus;
    // Only allow when logged in as USER; admin can use override separately
    const isLoggedInUser = !!token;
    return isLoggedInUser && !active && status !== 'requested' && status !== 'approved';
  })();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon color="primary" />
            Company Profile
          </Typography>
          {!isEditing ? (
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => setIsEditing(true)}
              >
                Edit Profile
              </Button>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<BoostIcon />}
                onClick={handleBoostProfile}
                disabled={!canRequestBoost}
              >
                {canRequestBoost ? 'Boost Profile' : 'Boost Pending/Active'}
              </Button>
            </Box>
          ) : (
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={loading}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </Box>
          )}
        </Box>

        {/* Profile Completion */}
        {(() => {
          const completion = calculateEmployerCompletion(profile);
          return (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Typography variant="h6">Profile Completion</Typography>
                      <Typography variant="h6">{completion.percent}%</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={completion.percent} sx={{ mb: 2 }} />
                    {completion.missingItems.length > 0 ? (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Missing information:
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {completion.missingItems.map(item => (
                            <Chip key={item.key} label={item.label} color="warning" variant="outlined" />
                          ))}
                        </Box>
                      </Box>
                    ) : (
                      <Alert severity="success">Your company profile is complete.</Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          );
        })()}

        <Grid container spacing={4}>
          {/* Company Logo and Basic Info */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Avatar
                  src={profile.logo || '/logo192.png'}
                  sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }}
                >
                  {profile.companyName?.[0]}
                </Avatar>
                {isEditing && (
                  <Box mb={2}>
                    <input
                      accept=".jpg,.jpeg,.png,.webp"
                      style={{ display: 'none' }}
                      id="upload-company-logo"
                      type="file"
                      onChange={(e) => handleLogoUpload(e)}
                    />
                    <label htmlFor="upload-company-logo">
                      <Button variant="outlined" startIcon={<AddIcon />} component="span">
                        Change Logo
                      </Button>
                    </label>
                  </Box>
                )}
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  {profile.companyName}
                  {profile.isVerified && <VerifiedIcon color="primary" />}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {profile.industry}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {profile.companySize}
                </Typography>
                {profile.foundedYear && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Founded: {profile.foundedYear}
                  </Typography>
                )}
              </CardContent>
            </Card>
        </Grid>

        {/* Verification Documents */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Verification Documents
          </Typography>
          <Grid container spacing={3}>
            {/* Business License */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Business License
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                    {(profile.businessLicense || []).map((doc, idx) => (
                      <Chip
                        key={idx}
                        label={doc.name || 'Document'}
                        onDelete={isEditing ? () => removeDoc('businessLicense', idx) : undefined}
                        deleteIcon={<DeleteIcon />}
                        variant="outlined"
                      />
                    ))}
                    {(!profile.businessLicense || profile.businessLicense.length === 0) && (
                      <Typography variant="body2" color="text.secondary">No documents uploaded</Typography>
                    )}
                  </Box>
                  {isEditing && (
                    <Box>
                      <input
                        accept=".pdf,.jpg,.jpeg,.png"
                        style={{ display: 'none' }}
                        id="upload-business-license"
                        type="file"
                        onChange={(e) => handleDocUpload('businessLicense', e)}
                      />
                      <label htmlFor="upload-business-license">
                        <Button variant="outlined" startIcon={<AddIcon />} component="span">
                          Upload Business License
                        </Button>
                      </label>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Company Registration */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Company Registration
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                    {(profile.companyRegistration || []).map((doc, idx) => (
                      <Chip
                        key={idx}
                        label={doc.name || 'Document'}
                        onDelete={isEditing ? () => removeDoc('companyRegistration', idx) : undefined}
                        deleteIcon={<DeleteIcon />}
                        variant="outlined"
                      />
                    ))}
                    {(!profile.companyRegistration || profile.companyRegistration.length === 0) && (
                      <Typography variant="body2" color="text.secondary">No documents uploaded</Typography>
                    )}
                  </Box>
                  {isEditing && (
                    <Box>
                      <input
                        accept=".pdf,.jpg,.jpeg,.png"
                        style={{ display: 'none' }}
                        id="upload-company-registration"
                        type="file"
                        onChange={(e) => handleDocUpload('companyRegistration', e)}
                      />
                      <label htmlFor="upload-company-registration">
                        <Button variant="outlined" startIcon={<AddIcon />} component="span">
                          Upload Company Registration
                        </Button>
                      </label>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Tax Certificate */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Tax Certificate
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                    {(profile.taxCertificate || []).map((doc, idx) => (
                      <Chip
                        key={idx}
                        label={doc.name || 'Document'}
                        onDelete={isEditing ? () => removeDoc('taxCertificate', idx) : undefined}
                        deleteIcon={<DeleteIcon />}
                        variant="outlined"
                      />
                    ))}
                    {(!profile.taxCertificate || profile.taxCertificate.length === 0) && (
                      <Typography variant="body2" color="text.secondary">No documents uploaded</Typography>
                    )}
                  </Box>
                  {isEditing && (
                    <Box>
                      <input
                        accept=".pdf,.jpg,.jpeg,.png"
                        style={{ display: 'none' }}
                        id="upload-tax-certificate"
                        type="file"
                        onChange={(e) => handleDocUpload('taxCertificate', e)}
                      />
                      <label htmlFor="upload-tax-certificate">
                        <Button variant="outlined" startIcon={<AddIcon />} component="span">
                          Upload Tax Certificate
                        </Button>
                      </label>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Other Documents */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Other Supporting Documents
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                    {(profile.otherDocuments || []).map((doc, idx) => (
                      <Chip
                        key={idx}
                        label={doc.name || 'Document'}
                        onDelete={isEditing ? () => removeDoc('otherDocuments', idx) : undefined}
                        deleteIcon={<DeleteIcon />}
                        variant="outlined"
                      />
                    ))}
                    {(!profile.otherDocuments || profile.otherDocuments.length === 0) && (
                      <Typography variant="body2" color="text.secondary">No documents uploaded</Typography>
                    )}
                  </Box>
                  {isEditing && (
                    <Box>
                      <input
                        accept=".pdf,.jpg,.jpeg,.png"
                        style={{ display: 'none' }}
                        id="upload-other-documents"
                        type="file"
                        onChange={(e) => handleDocUpload('otherDocuments', e)}
                      />
                      <label htmlFor="upload-other-documents">
                        <Button variant="outlined" startIcon={<AddIcon />} component="span">
                          Upload Document
                        </Button>
                      </label>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          <Alert severity="info" sx={{ mt: 2 }}>
            Uploaded documents are reviewed by admin. Upon approval, your company
            will receive a verification badge.
          </Alert>
        </Grid>

          {/* Company Details */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={profile.companyName}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={!isEditing}>
                  <InputLabel>Industry</InputLabel>
                  <Select
                    value={profile.industry}
                    onChange={(e) => handleInputChange('industry', e.target.value)}
                    label="Industry"
                  >
                    {industries.map((industry) => (
                      <MenuItem key={industry} value={industry}>
                        {industry}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={!isEditing}>
                  <InputLabel>Company Size</InputLabel>
                  <Select
                    value={profile.companySize}
                    onChange={(e) => handleInputChange('companySize', e.target.value)}
                    label="Company Size"
                  >
                    {companySizes.map((size) => (
                      <MenuItem key={size} value={size}>
                        {size}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Founded Year"
                  type="number"
                  value={profile.foundedYear}
                  onChange={(e) => handleInputChange('foundedYear', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Company Description"
                  value={profile.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Contact Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={profile.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  disabled={!isEditing}
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={profile.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  disabled={!isEditing}
                  InputProps={{
                    startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Website"
                  value={profile.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  disabled={!isEditing}
                  InputProps={{
                    startAdornment: <WebsiteIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Company Representative */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Company Representative
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Representative Name"
                  value={profile.representativeName}
                  onChange={(e) => handleInputChange('representativeName', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Representative Position"
                  value={profile.representativePosition}
                  onChange={(e) => handleInputChange('representativePosition', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Representative Email"
                  type="email"
                  value={profile.representativeEmail}
                  onChange={(e) => handleInputChange('representativeEmail', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Representative Phone"
                  value={profile.representativePhone}
                  onChange={(e) => handleInputChange('representativePhone', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Address Information */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationIcon />
              Address
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address"
                  value={profile.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={profile.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="State/Province"
                  value={profile.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Country"
                  value={profile.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="ZIP/Postal Code"
                  value={profile.zipCode}
                  onChange={(e) => handleInputChange('zipCode', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Specialties */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Specialties
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {profile.specialties.map((specialty, index) => (
                <Chip
                  key={index}
                  label={specialty}
                  color="primary"
                  variant="outlined"
                />
              ))}
              {profile.specialties.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No specialties added yet.
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Job Categories */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Job Categories
            </Typography>
            {isEditing ? (
              <FormControl fullWidth>
                <InputLabel>Job Categories</InputLabel>
                <Select
                  multiple
                  value={profile.jobCategories}
                  onChange={(e) => handleInputChange('jobCategories', e.target.value)}
                  label="Job Categories"
                >
                  {jobCategoryOptions.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Box display="flex" flexWrap="wrap" gap={1}>
                {(profile.jobCategories || []).map((cat, idx) => (
                  <Chip key={idx} label={cat} variant="outlined" />
                ))}
                {(!profile.jobCategories || profile.jobCategories.length === 0) && (
                  <Typography variant="body2" color="text.secondary">
                    No job categories selected.
                  </Typography>
                )}
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>

      <ProfileBoostPayment
        open={profileBoostOpen}
        onClose={() => setProfileBoostOpen(false)}
        userId={user?._id || user?.uid}
        isEmployer={true}
        settings={systemSettings}
        onPaid={handleProfileBoostPaid}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Profile;